import {
  doc,
  onSnapshot,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  limit,
  orderBy,
  arrayUnion,
  Timestamp,
  deleteField,
  getFirestore,
  DocumentReference,
  limitToLast,
  startAfter,
  endBefore,
} from "firebase/firestore";
import { db, dbCentral } from "../config/FirebaseConfig.js"; // Import the Firebase setup

export const convertSecondsToFirestoreTimestamp = (seconds) => {
  // Firestore expects milliseconds, so convert seconds to milliseconds
  const milliseconds = seconds * 1000;

  // Create and return a Firestore Timestamp
  return Timestamp.fromMillis(milliseconds);
};

export async function getDocDataById(collection, id) {
  if (!collection || !id) {
    console.error("Collection or ID is missing");
    return null;
  }
  let docRef = doc(db, collection, id);
  if (
    collection === "user" ||
    collection === "org" ||
    collection === "workspace"
  ) {
    docRef = doc(dbCentral, collection, id);
  }
  const docSnapshot = await getDoc(docRef);
  if (docSnapshot.exists()) {
    return { id: id, ref: docRef, ...docSnapshot.data() }; // Return plain object with document reference and ID
  }
  return null; // Return null if no document exists
}

export async function getDocDataByRef(docRef) {
  if (!docRef) {
    console.error("Document reference is missing");
    return null;
  }
  if (
    docRef.path === "user" ||
    docRef.path === "org" ||
    docRef.path === "workspace"
  ) {
    docRef = doc(dbCentral, docRef.path, docRef.id);
  }

  const docSnapshot = await getDoc(docRef);
  if (docSnapshot.exists()) {
    return { id: docSnapshot.id, ref: docRef, ...docSnapshot.data() }; // Return plain object with document reference and ID
  }
  return null; // Return null if no document exists
}

export function listenDocDataByRef(docRef, callback) {
  if (!docRef) {
    console.error("Document reference is missing");
    return null;
  }
  if (
    docRef.path === "user" ||
    docRef.path === "org" ||
    docRef.path === "workspace"
  ) {
    docRef = doc(dbCentral, docRef.path, docRef.id);
  }

  const unsubscribe = onSnapshot(
    docRef,
    (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = { id: docSnapshot.id, ref: docRef, ...docSnapshot.data() };
        callback(data);
      } else {
      }
    },
    (error) => {
      console.error("Error fetching folder:", error);
    }
  );

  return unsubscribe;
}

export function listenDocDataByRefX(docRef, callback, unsubscribeRef) {
  if (!docRef) {
    console.error("Document reference is missing");
    return null;
  }
  if (
    docRef.path === "user" ||
    docRef.path === "org" ||
    docRef.path === "workspace"
  ) {
    docRef = doc(dbCentral, docRef.path, docRef.id);
  }
  // Ensure we unsubscribe from the previous listener before creating a new one
  if (unsubscribeRef.current) {
    unsubscribeRef.current(); // Stop the old listener
  }

  // Set up a new listener
  unsubscribeRef.current = onSnapshot(
    docRef,
    (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = { id: docSnapshot.id, ref: docRef, ...docSnapshot.data() };
        callback(data);
      }
    },
    (error) => {
      console.error("Error fetching document:", error);
    }
  );

  // Return a cleanup function to stop listening when needed
  return () => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }
  };
}

export function listenDocDataById(collection, id, callback) {
  if (!collection || !id) {
    console.error("Collection or ID is missing");
    return null;
  }
  let docRef = doc(db, collection, id);
  if (
    collection === "user" ||
    collection === "org" ||
    collection === "workspace"
  ) {
    docRef = doc(dbCentral, collection, id);
  }

  return listenDocDataByRef(docRef, callback);
}

export function listenDocDataByIdX(collection, id, callback, unsubscribeRef) {
  let docRef = doc(db, collection, id);
  if (
    collection === "user" ||
    collection === "org" ||
    collection === "workspace"
  ) {
    docRef = doc(dbCentral, collection, id);
  }
  return listenDocDataByRefX(docRef, callback, unsubscribeRef);
}

export async function createDoc(collectionName, data) {
  // Create a reference to the collection
  const docRef = doc(collection(db, collectionName)); // Automatically generates a document ID
  // Write the document
  await setDoc(docRef, data);
  // Fetch the newly created document
  const docSnapshot = await getDoc(docRef);
  return { id: docSnapshot.id, ref: docRef, ...docSnapshot.data() }; // Return the document's data with its ID and reference
}

export async function deleteDocById(collection, id) {
  const docRef = doc(db, collection, id);
  await deleteDoc(docRef);
}

export function last_of_list(list) {
  return list[list.length - 1];
}

export async function deleteDocByRef(docRef) {
  await deleteDoc(docRef);
}

export async function getArrayDocRefByIDs(collection, ids) {
  if (!ids) {
    console.error("List of document IDs is missing");
    return [];
  }
  const docRefs = ids.map((id) => getRefFromId(collection, id));
  return docRefs;
}

export async function generateDocumentsXFromDocumentsXX(documentsXXRefs) {
  if (!documentsXXRefs) {
    console.error("List of documentXX references is missing");
    return [];
  }
  const documentsX = [];
  for (const docXXRef of documentsXXRefs) {
    const docXXData = await getDocDataByRef(docXXRef);
    if (docXXData) {
      const docXData = {
        name: docXXData.name,
        description: docXXData.description,
        custom: false,
        docXX_ref: docXXRef,
      };
      const docX = await createDoc("documentx", docXData);
      documentsX.push(docX);
    }
  }
  return documentsX;
}

export async function generateDocumentsXRefsFromDocumentsXX(documentsXXRefs) {
  if (!documentsXXRefs) {
    console.error("List of documentXX references is missing");
    return [];
  }
  const documentsXRefs = [];
  for (const docXXRef of documentsXXRefs) {
    const docXXData = await getDocDataByRef(docXXRef);
    if (docXXData) {
      const docXData = {
        name: docXXData.name,
        icon: docXXData.icon,
        infos: docXXData.infos,
        storage_path: null,
        state: "locked",
        documentxx: docXXRef,
      };
      const docX = await createDoc("documentx", docXData);
      documentsXRefs.push(docX.ref);
    }
  }
  return documentsXRefs;
}

export async function getArrayDocDataByRef(listdocRef) {
  if (!listdocRef) {
    console.error("List of document references is missing");
    return [];
  }
  if (listdocRef?.length === 0) {
    return [];
  }
  if (
    listdocRef[0].path === "user" ||
    listdocRef[0].path === "org" ||
    listdocRef[0].path === "workspace"
  ) {
    listdocRef = listdocRef.map((docRef) => {
      return doc(dbCentral, docRef.path, docRef.id);
    });
  }

  try {
    // Use Promise.all to fetch all documents concurrently
    const docSnapshots = await Promise.all(
      listdocRef.map((docRef) => getDoc(docRef))
    );
    // Filter and map the results to include only existing documents
    const docs = docSnapshots
      .filter((docSnapshot) => docSnapshot.exists())
      .map((docSnapshot) => ({
        id: docSnapshot.id,
        ref: docSnapshot.ref,
        ...docSnapshot.data(),
      }));

    return docs;
  } catch (error) {
    console.error("Error fetching documents:", error);
    return [];
  }
}

export function checkDB(docRef) {
  if (!docRef) {
    console.error("Document reference is missing");
    return null;
  }
  if (!(docRef instanceof DocumentReference)) {
    console.error("Invalid document reference provided");
    return null;
  }
  // If the document reference is from the central database, rebuild it
  if (
    docRef.path.startsWith("user/") ||
    docRef.path.startsWith("org/") ||
    docRef.path.startsWith("workspace/")
  ) {
    // Rebuild the document reference to point to the central database
    return doc(dbCentral, docRef.path);
  }
  return docRef; // Return the original reference if it's not from the central database
}

export async function getArrayDocDataByID(collection, listdocID) {
  if (!listdocID) {
    console.error("List of document references is missing");
    return [];
  }
  if (listdocID?.length === 0) {
    return [];
  }
  let listdocRef = [];
  for (const docID of listdocID) {
    const docRef = doc(db, collection, docID);
    listdocRef.push(checkDB(docRef));
  }

  try {
    // Use Promise.all to fetch all documents concurrently
    const docSnapshots = await Promise.all(
      listdocRef.map((docRef) => getDoc(docRef))
    );
    // Filter and map the results to include only existing documents
    const docs = docSnapshots
      .filter((docSnapshot) => docSnapshot.exists())
      .map((docSnapshot) => ({
        id: docSnapshot.id,
        ref: docSnapshot.ref,
        ...docSnapshot.data(),
      }));

    return docs;
  } catch (error) {
    console.error("Error fetching documents:", error);
    return [];
  }
}

export function getFlutterTime() {
  return Timestamp.now();
}

export function getFlutterTimeFromSeconds(seconds) {
  return Timestamp.fromMillis(seconds * 1000);
}

export function getDateFromFlutter(flutter_date) {
  if (!flutter_date) {
    return null;
  }
  return flutter_date.toDate();
}
export function getLocalStringFromFlutter(flutter_date) {
  if (!flutter_date?.seconds) {
    return null;
  }
  return new Date(flutter_date.seconds * 1000).toLocaleString();
}

export function getDateFromFirebase(start) {
  return new Date(start.seconds * 1000);
}

export function getDateFromSeconds(seconds) {
  return new Date(seconds * 1000);
}
export function getFlutterTimeFromSecondsFormatDate(seconds) {
  // to ("DD/MM/YYYY")
  return getFlutterTimeFromSeconds(seconds)
    .toDate()
    .toLocaleDateString("fr-FR");
}

export function getFlutterTimeFromSecondsFormatTime(seconds) {
  // to ("HH:mm")
  return getFlutterTimeFromSeconds(seconds)
    .toDate()
    .toLocaleTimeString("fr-FR");
  // to ("DD/MM/YYYY")
}

export async function updateDocDataField(docRef, field, value) {
  await setDoc(docRef, { [field]: value }, { merge: true });
}

export async function deleteDocDataField(docRef, field) {
  await setDoc(docRef, { [field]: deleteField() }, { merge: true });
}

export async function addToArrayField(docRef, field, item) {
  await setDoc(
    docRef,
    { [field]: arrayUnion(item) }, // Use arrayUnion to add item to array
    { merge: true } // Ensure existing data is not overwritten
  );
}

export function getRefFromId(collection, id) {
  return doc(db, collection, id);
}

export async function getDocumentsByFolderRef(folderRef) {
  const docSnapshot = await getDoc(folderRef);
  const docData = docSnapshot.data();
  const docs = [];

  for (const docRef of docData?.documents || []) {
    const docSnapshot = await getDoc(docRef);
    if (docSnapshot.exists()) {
      docs.push({
        id: docRef.id,
        ref: docRef,
        ...docSnapshot.data(),
      }); // Include document reference and ID
    }
  }
  return docs || [];
}

export function TruncatedString(text, maxLength = 20) {
  const truncatedText =
    text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  return truncatedText;
}

// Dynamic query function
export async function queryCollection(
  collectionName,
  filters,
  limite = 10000,
  sortingField = null,
  sortingOrder = "asc"
) {
  if (!collectionName) {
    console.error("Collection name is missing");
    return null;
  }

  // Initialize query with the collection reference
  let objectsQuery = collection(db, collectionName);
  if (collectionName === "user" || collectionName === "org") {
    objectsQuery = collection(dbCentral, collectionName);
  }

  // Apply dynamic filters if provided
  if (filters && filters.length > 0) {
    const queries = filters.map((filter) =>
      where(filter.field, filter.operator, filter.value)
    );
    objectsQuery = query(objectsQuery, ...queries);
  }

  // Apply sorting if a sortingField is provided
  if (sortingField) {
    objectsQuery = query(objectsQuery, orderBy(sortingField, sortingOrder));
  }

  // Apply the limit to the query
  objectsQuery = query(objectsQuery, limit(limite));

  try {
    // Fetch the documents that match the query
    const querySnapshot = await getDocs(objectsQuery);

    // Map the documents to an array of data with document ID and reference
    const objects = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ref: doc.ref,
      ...doc.data(),
    }));

    return objects || [];
  } catch (error) {
    console.error("Error querying collection: ", collectionName, error);
    return null;
  }
}

// Progressive query function with cursor-based pagination
export async function queryCollectionProgressive(
  collectionName,
  filters = [],
  pageSize = 50,
  sortingField = null,
  sortingOrder = "asc",
  cursor = null,
  direction = "next" // "next" or "previous"
) {
  if (!collectionName) {
    console.error("Collection name is missing");
    return { data: null, error: "Collection name is missing" };
  }

  try {
    // Initialize query with the collection reference
    let objectsQuery = collection(db, collectionName);
    if (collectionName === "user" || collectionName === "org") {
      objectsQuery = collection(dbCentral, collectionName);
    }

    // Apply dynamic filters if provided
    console.log(
      `Querying collection: ${collectionName} with filters:`,
      filters,
      cursor,
      direction
    );
    if (filters && filters.length > 0) {
      const queries = filters.map((filter) =>
        where(filter.field, filter.operator, filter.value)
      );
      objectsQuery = query(objectsQuery, ...queries);
    }

    // Apply sorting - required for cursor pagination
    // If no sortingField provided, use document ID as default
    const sortField = sortingField || "__name__";
    const sortOrder = sortingOrder;

    if (direction === "previous") {
      // For previous page, reverse the order and use endBefore
      const reversedOrder = sortOrder === "asc" ? "desc" : "asc";
      objectsQuery = query(objectsQuery, orderBy(sortField, reversedOrder));

      if (cursor) {
        objectsQuery = query(objectsQuery, endBefore(cursor));
      }
      objectsQuery = query(objectsQuery, limitToLast(pageSize));
    } else {
      // For next page (default behavior)
      //objectsQuery = query(objectsQuery, orderBy(sortField, sortOrder));

      if (cursor) {
        objectsQuery = query(objectsQuery, startAfter(cursor));
      }
      objectsQuery = query(objectsQuery, limit(pageSize));
    }

    // Fetch the documents that match the query
    const querySnapshot = await getDocs(objectsQuery);
    console.log(
      `Fetched ${querySnapshot.docs.length} documents from collection: ${collectionName}`
    );

    // Map the documents to an array of data with document ID and reference
    let objects = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ref: doc.ref,
      ...doc.data(),
    }));

    // If we reversed order for previous page, reverse the results back
    if (direction === "previous") {
      objects = objects.reverse();
    }

    // Get cursors for pagination
    const firstDoc = querySnapshot.docs[0];
    const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];

    return {
      data: objects,
      pagination: {
        hasData: objects.length > 0,
        count: objects.length,
        pageSize,
        firstCursor: firstDoc ? firstDoc : null,
        lastCursor: lastDoc ? lastDoc : null,
        hasNext: objects.length === pageSize, // Assume there's more if we got a full page
        hasPrevious: cursor !== null, // If we have a cursor, we can go back
      },
      error: null,
    };
  } catch (error) {
    console.error("Error querying collection:", collectionName, error);
    return {
      data: null,
      pagination: null,
      error: error.message,
    };
  }
}

// Helper function to get the first page
export async function getFirstPage(
  collection,
  filters = [],
  pageSize = 50,
  sortingField = "__name__",
  sortingOrder = "asc"
) {
  console.log(
    "Fetching first page of collection:",
    collection,
    filters,
    pageSize,
    sortingField,
    sortingOrder
  );
  return await queryCollectionProgressive(
    collection,
    filters,
    pageSize,
    sortingField,
    sortingOrder,
    null,
    "next"
  );
}

// Helper function to get the next page
export async function getNextPage(
  collection,
  filters = [],
  pageSize = 50,
  sortingField = "__name__",
  sortingOrder = "asc",
  lastCursor
) {
  return await queryCollectionProgressive(
    collection,
    filters,
    pageSize,
    sortingField,
    sortingOrder,
    lastCursor,
    "next"
  );
}

// Helper function to get the previous page
export async function getPreviousPage(
  collection,
  filters = [],
  pageSize = 50,
  sortingField = "__name__",
  sortingOrder = "asc",
  firstCursor
) {
  return await queryCollectionProgressive(
    collection,
    filters,
    pageSize,
    sortingField,
    sortingOrder,
    firstCursor,
    "previous"
  );
}

// Dynamic query function with active listening
export function listenCollection(
  collectionName,
  filters,
  limite = 1000,
  sortingField = null,
  sortingOrder = "asc",
  onDataChange,
  unsubscribeRef = null,
  onError
) {
  if (!collectionName) {
    console.error("Collection name is missing");
    return null;
  }

  if (unsubscribeRef?.current) {
    unsubscribeRef.current();
  }

  const db = getFirestore();

  // Initialize query with the collection reference
  let objectsQuery = collection(db, collectionName);

  // Apply dynamic filters if provided
  if (filters && filters.length > 0) {
    const queries = filters.map((filter) =>
      where(filter.field, filter.operator, filter.value)
    );
    objectsQuery = query(objectsQuery, ...queries);
  }

  // Apply sorting if a sortingField is provided
  if (sortingField) {
    objectsQuery = query(objectsQuery, orderBy(sortingField, sortingOrder));
  }

  // Apply the limit to the query
  objectsQuery = query(objectsQuery, limit(limite));

  try {
    // Set up a listener for the query

    let current = onSnapshot(
      objectsQuery,
      (querySnapshot) => {
        const objects = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ref: doc.ref,
          ...doc.data(),
        }));

        // Call the onDataChange callback with the updated data
        if (onDataChange) {
          onDataChange(objects);
        }
      },
      (error) => {
        console.error("Error listening to collection:", error);
        if (onError) {
          onError(error);
        }
      }
    );

    if (unsubscribeRef) {
      unsubscribeRef.current = current;
    }

    return () => {
      if (unsubscribeRef?.current) {
        unsubscribeRef.current();
      }
    };
  } catch (error) {
    console.error("Error setting up snapshot listener:", error);
    return null;
  }
}
